import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Pencil, Save, X, ExternalLink } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = ["Events", "Music", "Inquiries", "Social Links"];

const emptyEvent = { title: "", date_label: "", time_label: "", location: "", description: "", image_url: "", rsvp_url: "", sort_order: 0 };
const emptyMusic = { title: "", frequency: "", description: "", youtube_id: "", sort_order: 0 };

function field(label, value, onChange, props = {}) {
  return (
    <div>
      <label className="font-accent text-xs text-[#3a2630]/70">{label}</label>
      <input
        value={value}
        onChange={onChange}
        {...props}
        className="mt-1 w-full rounded-lg border border-[#e3b4b0]/60 bg-[#f7efe2] px-3 py-2 text-sm outline-none focus:border-[#b76e79]"
      />
    </div>
  );
}

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Events");
  const [events, setEvents] = useState([]);
  const [music, setMusic] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [settings, setSettings] = useState({ youtube_url: "", tiktok_url: "", soundcloud_url: "", instagram_url: "" });

  const [eventDraft, setEventDraft] = useState(null);
  const [musicDraft, setMusicDraft] = useState(null);

  const loadAll = () => {
    api.get("/events").then((r) => setEvents(r.data));
    api.get("/music").then((r) => setMusic(r.data));
    api.get("/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data));
  };

  useEffect(loadAll, []);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const fail = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");

  // Events
  const saveEvent = async () => {
    try {
      if (eventDraft.id) await api.put(`/events/${eventDraft.id}`, eventDraft);
      else await api.post("/events", eventDraft);
      toast.success("Event saved");
      setEventDraft(null);
      api.get("/events").then((r) => setEvents(r.data));
    } catch (e) { fail(e); }
  };
  const delEvent = async (id) => {
    try { await api.delete(`/events/${id}`); api.get("/events").then((r) => setEvents(r.data)); toast.success("Deleted"); } catch (e) { fail(e); }
  };

  // Music
  const saveMusic = async () => {
    try {
      if (musicDraft.id) await api.put(`/music/${musicDraft.id}`, musicDraft);
      else await api.post("/music", musicDraft);
      toast.success("Track saved");
      setMusicDraft(null);
      api.get("/music").then((r) => setMusic(r.data));
    } catch (e) { fail(e); }
  };
  const delMusic = async (id) => {
    try { await api.delete(`/music/${id}`); api.get("/music").then((r) => setMusic(r.data)); toast.success("Deleted"); } catch (e) { fail(e); }
  };

  const delInquiry = async (id) => {
    try { await api.delete(`/inquiries/${id}`); setInquiries((x) => x.filter((i) => i.id !== id)); } catch (e) { fail(e); }
  };

  const saveSettings = async () => {
    try { await api.put("/settings", settings); toast.success("Social links saved"); } catch (e) { fail(e); }
  };

  return (
    <div className="min-h-screen bg-[#efe1cd]">
      <header className="bg-[#281620] text-[#f7efe2] sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-display text-xl">Rosefield <span className="text-[#c2a04b]">Admin</span></span>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="font-accent text-sm text-[#e3b4b0] hover:text-[#f7efe2] flex items-center gap-1">
              <ExternalLink size={15} /> View site
            </a>
            <button data-testid="admin-logout" onClick={doLogout} className="font-accent text-sm flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#b76e79] hover:bg-[#9c5560] transition-colors">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`tab-${t.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => setTab(t)}
              className={`font-accent text-sm px-5 py-2.5 rounded-full transition-colors ${
                tab === t ? "bg-[#b76e79] text-[#f7efe2]" : "bg-[#fbf6ec] text-[#3a2630] hover:bg-[#e3b4b0]/40"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* EVENTS */}
        {tab === "Events" && (
          <div data-testid="events-panel">
            <button onClick={() => setEventDraft({ ...emptyEvent })} data-testid="add-event" className="mb-5 inline-flex items-center gap-2 font-accent text-sm px-4 py-2 rounded-full bg-[#281620] text-[#f7efe2] hover:bg-[#9c5560]">
              <Plus size={16} /> Add Event
            </button>
            {eventDraft && (
              <div className="bg-[#fbf6ec] rounded-xl p-5 mb-6 border border-[#b76e79]/40 grid sm:grid-cols-2 gap-4" data-testid="event-editor">
                {field("Title", eventDraft.title, (e) => setEventDraft({ ...eventDraft, title: e.target.value }), { "data-testid": "event-title" })}
                {field("Date label", eventDraft.date_label, (e) => setEventDraft({ ...eventDraft, date_label: e.target.value }), { placeholder: "Saturday, July 20, 2026" })}
                {field("Time label", eventDraft.time_label, (e) => setEventDraft({ ...eventDraft, time_label: e.target.value }), { placeholder: "7:00 PM EST" })}
                {field("Location", eventDraft.location, (e) => setEventDraft({ ...eventDraft, location: e.target.value }), { placeholder: "Sedona, AZ / Online" })}
                {field("Image URL (optional)", eventDraft.image_url, (e) => setEventDraft({ ...eventDraft, image_url: e.target.value }))}
                {field("RSVP URL (optional)", eventDraft.rsvp_url, (e) => setEventDraft({ ...eventDraft, rsvp_url: e.target.value }))}
                {field("Sort order", eventDraft.sort_order, (e) => setEventDraft({ ...eventDraft, sort_order: Number(e.target.value) }), { type: "number" })}
                <div className="sm:col-span-2">
                  <label className="font-accent text-xs text-[#3a2630]/70">Description</label>
                  <textarea value={eventDraft.description} onChange={(e) => setEventDraft({ ...eventDraft, description: e.target.value })} rows={3} className="mt-1 w-full rounded-lg border border-[#e3b4b0]/60 bg-[#f7efe2] px-3 py-2 text-sm outline-none focus:border-[#b76e79] resize-none" />
                </div>
                <div className="sm:col-span-2 flex gap-3">
                  <button onClick={saveEvent} data-testid="save-event" className="inline-flex items-center gap-1.5 font-accent text-sm px-5 py-2 rounded-full bg-[#b76e79] text-[#f7efe2] hover:bg-[#9c5560]"><Save size={15} /> Save</button>
                  <button onClick={() => setEventDraft(null)} className="inline-flex items-center gap-1.5 font-accent text-sm px-5 py-2 rounded-full bg-[#e3b4b0]/40 text-[#3a2630]"><X size={15} /> Cancel</button>
                </div>
              </div>
            )}
            <div className="space-y-3">
              {events.map((e) => (
                <div key={e.id} data-testid={`event-row-${e.id}`} className="bg-[#fbf6ec] rounded-xl p-4 flex items-center justify-between border border-[#e3b4b0]/40">
                  <div>
                    <p className="font-display text-lg text-[#3a2630]">{e.title}</p>
                    <p className="text-xs text-[#3a2630]/60 font-accent">{[e.date_label, e.time_label, e.location].filter(Boolean).join(" · ")}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEventDraft(e)} className="p-2 rounded-full hover:bg-[#e3b4b0]/40 text-[#9c5560]"><Pencil size={16} /></button>
                    <button onClick={() => delEvent(e.id)} data-testid={`delete-event-${e.id}`} className="p-2 rounded-full hover:bg-red-100 text-red-600"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MUSIC */}
        {tab === "Music" && (
          <div data-testid="music-panel">
            <p className="text-sm text-[#3a2630]/70 mb-4 font-accent">Paste the YouTube video ID (the part after <span className="text-[#9c5560]">watch?v=</span>) to enable hover preview & playback.</p>
            <button onClick={() => setMusicDraft({ ...emptyMusic })} data-testid="add-music" className="mb-5 inline-flex items-center gap-2 font-accent text-sm px-4 py-2 rounded-full bg-[#281620] text-[#f7efe2] hover:bg-[#9c5560]">
              <Plus size={16} /> Add Track
            </button>
            {musicDraft && (
              <div className="bg-[#fbf6ec] rounded-xl p-5 mb-6 border border-[#b76e79]/40 grid sm:grid-cols-2 gap-4" data-testid="music-editor">
                {field("Title", musicDraft.title, (e) => setMusicDraft({ ...musicDraft, title: e.target.value }), { "data-testid": "music-title" })}
                {field("Frequency / subtitle", musicDraft.frequency, (e) => setMusicDraft({ ...musicDraft, frequency: e.target.value }), { placeholder: "432 Hz · Universal Harmony" })}
                {field("YouTube video ID", musicDraft.youtube_id, (e) => setMusicDraft({ ...musicDraft, youtube_id: e.target.value }), { "data-testid": "music-youtube-id", placeholder: "dQw4w9WgXcQ" })}
                {field("Sort order", musicDraft.sort_order, (e) => setMusicDraft({ ...musicDraft, sort_order: Number(e.target.value) }), { type: "number" })}
                <div className="sm:col-span-2">
                  <label className="font-accent text-xs text-[#3a2630]/70">Description</label>
                  <textarea value={musicDraft.description} onChange={(e) => setMusicDraft({ ...musicDraft, description: e.target.value })} rows={2} className="mt-1 w-full rounded-lg border border-[#e3b4b0]/60 bg-[#f7efe2] px-3 py-2 text-sm outline-none focus:border-[#b76e79] resize-none" />
                </div>
                <div className="sm:col-span-2 flex gap-3">
                  <button onClick={saveMusic} data-testid="save-music" className="inline-flex items-center gap-1.5 font-accent text-sm px-5 py-2 rounded-full bg-[#b76e79] text-[#f7efe2] hover:bg-[#9c5560]"><Save size={15} /> Save</button>
                  <button onClick={() => setMusicDraft(null)} className="inline-flex items-center gap-1.5 font-accent text-sm px-5 py-2 rounded-full bg-[#e3b4b0]/40 text-[#3a2630]"><X size={15} /> Cancel</button>
                </div>
              </div>
            )}
            <div className="space-y-3">
              {music.map((m) => (
                <div key={m.id} data-testid={`music-row-${m.id}`} className="bg-[#fbf6ec] rounded-xl p-4 flex items-center justify-between border border-[#e3b4b0]/40">
                  <div>
                    <p className="font-display text-lg text-[#3a2630]">{m.title}</p>
                    <p className="text-xs text-[#3a2630]/60 font-accent">{m.frequency} {m.youtube_id ? `· ${m.youtube_id}` : "· no video yet"}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setMusicDraft(m)} className="p-2 rounded-full hover:bg-[#e3b4b0]/40 text-[#9c5560]"><Pencil size={16} /></button>
                    <button onClick={() => delMusic(m.id)} data-testid={`delete-music-${m.id}`} className="p-2 rounded-full hover:bg-red-100 text-red-600"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* INQUIRIES */}
        {tab === "Inquiries" && (
          <div data-testid="inquiries-panel" className="space-y-3">
            {inquiries.length === 0 && <p className="text-[#3a2630]/60 font-accent">No inquiries yet.</p>}
            {inquiries.map((i) => (
              <div key={i.id} data-testid={`inquiry-${i.id}`} className="bg-[#fbf6ec] rounded-xl p-5 border border-[#e3b4b0]/40">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-lg text-[#3a2630]">{i.name} <span className="font-accent text-xs px-2 py-0.5 rounded-full bg-[#e3b4b0]/40 text-[#9c5560] ml-1">{i.inquiry_type}</span></p>
                    <a href={`mailto:${i.email}`} className="text-sm text-[#b76e79]">{i.email}</a>
                    <p className="text-sm text-[#3a2630]/80 mt-2 leading-relaxed">{i.message}</p>
                    <p className="text-[11px] text-[#3a2630]/50 mt-2 font-accent">{new Date(i.created_at).toLocaleString()} · {i.forwarded ? "forwarded ✓" : "stored"}</p>
                  </div>
                  <button onClick={() => delInquiry(i.id)} className="p-2 rounded-full hover:bg-red-100 text-red-600 shrink-0"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* SOCIAL */}
        {tab === "Social Links" && (
          <div data-testid="social-panel" className="bg-[#fbf6ec] rounded-xl p-6 border border-[#e3b4b0]/40 max-w-lg space-y-4">
            {field("YouTube URL", settings.youtube_url, (e) => setSettings({ ...settings, youtube_url: e.target.value }), { "data-testid": "settings-youtube" })}
            {field("TikTok URL", settings.tiktok_url, (e) => setSettings({ ...settings, tiktok_url: e.target.value }), { "data-testid": "settings-tiktok" })}
            {field("SoundCloud URL", settings.soundcloud_url, (e) => setSettings({ ...settings, soundcloud_url: e.target.value }))}
            {field("Instagram URL", settings.instagram_url, (e) => setSettings({ ...settings, instagram_url: e.target.value }))}
            <button onClick={saveSettings} data-testid="save-settings" className="inline-flex items-center gap-1.5 font-accent text-sm px-5 py-2 rounded-full bg-[#b76e79] text-[#f7efe2] hover:bg-[#9c5560]"><Save size={15} /> Save Links</button>
          </div>
        )}
      </div>
    </div>
  );
}
